var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { FloatingList, useFloating, useInteractions, useListNavigation, useTypeahead } from '@floating-ui/react';
import { clsx } from 'clsx';
import { PropsChecker } from '@telia-front/props-checker';
import { ListboxContext } from './listbox-context';
/**
 * A listbox displays a list of options and allows a user to select one of them.
 */
export const Listbox = (props) => {
    return (React.createElement(PropsChecker, { componentProps: props, componentName: "Listbox", defaultValueName: "defaultValue", valueName: "value", onChangeNames: "onChange" },
        React.createElement(ListboxInner, Object.assign({}, props))));
};
const ListboxInner = (_a) => {
    var { children, defaultValue = null, value: propValue, onChange, className } = _a, rest = __rest(_a, ["children", "defaultValue", "value", "onChange", "className"]);
    const [innerValue, setInnerValue] = React.useState(defaultValue);
    const value = onChange && typeof propValue !== 'undefined' ? propValue : innerValue;
    const [activeIndex, setActiveIndex] = React.useState(null);
    const { context, refs } = useFloating({
        open: true, // Always open floating hook, so we could use interaction hooks.
    });
    const elementsRef = React.useRef([]);
    const labelsRef = React.useRef([]);
    const [values, setValues] = React.useState([]);
    const selectedIndex = value ? values.indexOf(value) : null;
    const listNav = useListNavigation(context, {
        listRef: elementsRef,
        activeIndex,
        selectedIndex,
        onNavigate: setActiveIndex,
        focusItemOnHover: false,
    });
    const typeahead = useTypeahead(context, {
        listRef: labelsRef,
        activeIndex,
        selectedIndex,
        onMatch: setActiveIndex,
    });
    const { getFloatingProps, getItemProps } = useInteractions([listNav, typeahead]);
    const handleSelect = React.useCallback((nextValue) => {
        if (typeof propValue === 'undefined') {
            setInnerValue(nextValue);
        }
        if (onChange) {
            onChange(nextValue);
        }
    }, [propValue, onChange]);
    const registerValue = React.useCallback((value, index) => {
        setValues(prevValues => {
            const newArray = [...prevValues];
            newArray[index] = value;
            return newArray;
        });
    }, []);
    const unregisterValue = React.useCallback((value) => {
        setValues(prevValues => {
            return [...prevValues].filter(i => i !== value);
        });
    }, []);
    return (React.createElement("ul", Object.assign({}, rest, { className: clsx('listbox', className), role: "listbox", ref: refs.setFloating }, getFloatingProps()),
        React.createElement(ListboxContext.Provider, { value: React.useMemo(() => ({
                activeIndex,
                selectedIndex,
                getItemProps,
                handleSelect,
                registerValue,
                unregisterValue,
            }), [activeIndex, selectedIndex, getItemProps, handleSelect, registerValue, unregisterValue]) },
            React.createElement(FloatingList, { elementsRef: elementsRef, labelsRef: labelsRef }, children))));
};
